import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import qs from 'qs';
import _get from 'lodash/get';
import useSiteConfigSelector from 'components/useSiteConfigSelector';
import useIsAuthPath from 'components/Hooks/useIsAuthPath';
import { makeSelectAuth } from 'containers/Auth/selectors';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import { hasClientAccessToFeature } from 'utils/clientSettings';
import { getLoginSource } from 'utils/sourceTrackingService';
import useIsOnboardingEnabled from 'containers/OnboardingModal/hooks/useIsOnboardingEnabled';
import { makeSelectOnboardingModalsLoading } from 'containers/OnboardingModal/selectors';
import { getLocalData } from 'utils/localDataStore';
import { getSubdomain } from 'utils/stringUtils';
import { getCustomOnboardingLocalUtils } from 'containers/OnboardingModal/utils';

export default function useEmailLoginReminder() {
  const location = useLocation();
  const history = useHistory();
  const auth = useSelector(makeSelectAuth());
  const clientDetails = useSelector(makeSelectClientDetails());
  const allowSignIn = clientDetails?.signIn === 'Allow';
  const [siteConfig] = useSiteConfigSelector(['Features']);
  const [open, setOpen] = useState();
  const [reminderWasShown, setReminderWasShown] = useState();
  const [reminderDismissed, setReminderDismissed] = useState(false);
  const isAuthPage = useIsAuthPath();
  const { showRedirect, showReporting, showProfile } = useIsOnboardingEnabled();
  const isCustomOnboardingActive = showRedirect || showReporting || showProfile;
  const onboardingModalsLoading = useSelector(
    makeSelectOnboardingModalsLoading,
  );

  const isCTAModalEnabled =
    allowSignIn &&
    hasClientAccessToFeature({
      featuresSiteConfig: siteConfig,
      featureName: 'authenticationCallToActionModal',
      clientDetails,
    });

  const query = useMemo(() => {
    const search = qs.parse(location.search, { ignoreQueryPrefix: true });
    const hashQuery = location.hash.split('?')[1] || '';
    const hash = qs.parse(hashQuery);
    return { ...search, ...hash };
  }, [location.search, location.hash]);

  const shouldShowReminder = useMemo(
    () =>
      query.utm_source === 'email' ||
      (auth.isLoaded && getLoginSource()?.lastLoginSource === 'email'),
    [auth.isLoaded, query],
  );

  const isLoginReminderFeatureEnabled = useMemo(
    () =>
      hasClientAccessToFeature({
        featuresSiteConfig: siteConfig,
        featureName: 'loginReminder',
        clientDetails,
      }),
    [clientDetails, siteConfig],
  );

  const { getDismissed } = getCustomOnboardingLocalUtils();

  const [onboardingDismissedState, setOnboardingDismissedState] = useState(
    () => !!getDismissed(),
  );

  useEffect(() => {
    if (!auth.isLoaded || !auth.isEmpty) {
      return undefined;
    }

    const checkDismissedState = () => {
      const isDismissed = !!getDismissed();
      setOnboardingDismissedState(isDismissed);
    };

    checkDismissedState();

    const handleStorageChange = e => {
      const brand = getSubdomain() || getLocalData('brand');
      const dismissedKey = `${brand}_customOnboardingDismissed`;
      if (e.key === dismissedKey || e.key === null) {
        checkDismissedState();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    const intervalId = setInterval(checkDismissedState, 300);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, [auth.isLoaded, auth.isEmpty]);

  useEffect(() => {
    if ((isCustomOnboardingActive || onboardingModalsLoading) && open) {
      setOpen(false);
    }
  }, [isCustomOnboardingActive, onboardingModalsLoading, open]);

  useEffect(() => {
    if (!auth.isLoaded) return;

    if (onboardingModalsLoading) return;

    const isOnboardingActuallyActive =
      isCustomOnboardingActive && !onboardingDismissedState;

    const shouldShow =
      auth.isEmpty &&
      allowSignIn &&
      isLoginReminderFeatureEnabled &&
      shouldShowReminder &&
      !isAuthPage &&
      !isCTAModalEnabled &&
      !isOnboardingActuallyActive;

    if (shouldShow && !open && !reminderDismissed) {
      setOpen(true);
      setReminderWasShown(true);
    }
  }, [
    auth,
    allowSignIn,
    isLoginReminderFeatureEnabled,
    shouldShowReminder,
    isAuthPage,
    isCTAModalEnabled,
    isCustomOnboardingActive,
    onboardingModalsLoading,
    open,
    reminderDismissed,
    onboardingDismissedState,
  ]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setReminderDismissed(true);
  }, []);

  const handleLogin = useCallback(() => {
    const redirect = encodeURIComponent(
      `${location.pathname}${location.search}`,
    );
    const hasCustomAuthenticationEnabled = _get(
      clientDetails,
      'customAuthentication.enabled',
    );
    const pathname = hasCustomAuthenticationEnabled ? '/auth' : '/login';
    setOpen(false);
    history.push({ pathname, search: `?redirect=${redirect}` });
  }, [clientDetails, history, location.pathname, location.search]);

  return { open, handleClose, handleLogin, reminderWasShown };
}
